/**
 * Drag-and-drop batch upload zone for multiple PDFs.
 *
 * Features:
 * - Drag-and-drop or click to select files
 * - Staged files list with remove buttons
 * - Upload all files in parallel
 * - Progress indicator for batch
 */

import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Upload,
  FileText,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface StagedFile {
  id: string;
  file: File;
  status: "staged" | "uploading" | "completed" | "failed";
  error?: string;
}

interface BatchUploadZoneProps {
  /** Callback when files should be uploaded */
  onUpload: (files: File[]) => Promise<void>;
  /** Whether upload is in progress */
  isUploading?: boolean;
  /** Number of completed uploads in current batch */
  completedCount?: number;
  /** Total files in current batch */
  totalCount?: number;
  /** Optional className */
  className?: string;
}

export function BatchUploadZone({
  onUpload,
  isUploading = false,
  completedCount = 0,
  totalCount = 0,
  className,
}: BatchUploadZoneProps) {
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate unique ID for staged files
  const generateId = () => Math.random().toString(36).substring(7);

  // Handle file selection (from input or drop)
  const handleFiles = useCallback((files: FileList | File[]) => {
    const pdfFiles = Array.from(files).filter(
      (file) => file.type === "application/pdf"
    );

    if (pdfFiles.length === 0) return;

    const newStagedFiles: StagedFile[] = pdfFiles.map((file) => ({
      id: generateId(),
      file,
      status: "staged",
    }));

    setStagedFiles((prev) => [...prev, ...newStagedFiles]);
  }, []);

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (e.dataTransfer.files) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  // Handle file input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
      // Reset input
      e.target.value = "";
    },
    [handleFiles]
  );

  // Remove a staged file
  const removeFile = useCallback((id: string) => {
    setStagedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  // Clear all staged files
  const clearAll = useCallback(() => {
    setStagedFiles([]);
  }, []);

  // Upload all staged files
  const handleUploadAll = useCallback(async () => {
    if (stagedFiles.length === 0 || isUploading) return;

    const files = stagedFiles.map((sf) => sf.file);

    try {
      await onUpload(files);
      // Clear staged files on success
      setStagedFiles([]);
    } catch (error) {
      // Error handling done by parent
    }
  }, [stagedFiles, isUploading, onUpload]);

  // Click to open file dialog
  const handleClick = useCallback(() => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  }, [isUploading]);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Drop Zone */}
      <Card
        className={cn(
          "border-dashed transition-colors",
          isDragOver && "border-primary bg-primary/5",
          isUploading && "pointer-events-none opacity-60"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8">
            <div
              className={cn(
                "rounded-full p-4 mb-4 transition-colors",
                isDragOver ? "bg-primary/20" : "bg-primary/10"
              )}
            >
              <Upload
                className={cn(
                  "h-8 w-8 transition-colors",
                  isDragOver ? "text-primary" : "text-primary"
                )}
              />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              {isDragOver ? "Drop files here" : "Upload Financial Documents"}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 text-center max-w-sm">
              Drag and drop PDF files, or click to select. You can upload
              multiple files at once.
            </p>
            <Button
              type="button"
              variant="outline"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4" />
              Select PDF Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,application/pdf"
              onChange={handleInputChange}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>

      {/* Staged Files List */}
      {stagedFiles.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium">
                {stagedFiles.length} file{stagedFiles.length !== 1 ? "s" : ""}{" "}
                ready to upload
              </h4>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAll}
                  disabled={isUploading}
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={handleUploadAll}
                  disabled={isUploading || stagedFiles.length === 0}
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload All
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress bar during upload */}
            {isUploading && totalCount > 0 && (
              <div className="mb-3">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">
                    Processing {completedCount} of {totalCount}
                  </span>
                  <span className="font-medium">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}

            {/* File list */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {stagedFiles.map((sf) => (
                <div
                  key={sf.id}
                  className="flex items-center gap-3 p-2 rounded-lg border bg-muted/30"
                >
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{sf.file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(sf.file.size)}
                    </p>
                  </div>
                  {sf.status === "staged" && !isUploading && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(sf.id);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  {sf.status === "uploading" && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  )}
                  {sf.status === "completed" && (
                    <CheckCircle2 className="h-4 w-4 text-success" />
                  )}
                  {sf.status === "failed" && (
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default BatchUploadZone;
